// errorApi.ts - API service for error management (angepasst an echte DB Struktur)
import apiClient from './apiClient';

interface ErrorLog {
  id: string;
  error_code: string;
  timestamp: string;
  path: string;
  method: string;
  error_message: string;
  user_id?: string;
  context_info?: string;
  created_at: string;
  updated_at: string;
}

interface ErrorLogDetail extends ErrorLog {
  stacktrace?: string;
}

interface ErrorLogCreate {
  path: string;
  method?: string;
  error_message: string;
  stacktrace?: string;
  user_id?: string;
  context_info?: string;
}

interface ErrorStats {
  total_errors: number;
  errors_today: number;
  errors_this_week: number;
  most_common_paths: Array<{ path: string; count: number }>;
  error_methods_distribution: Record<string, number>;
  recent_errors: ErrorLog[];
}

interface ErrorQueryParams {
  skip?: number;
  limit?: number;
  path?: string;
  method?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

class ErrorApi {

  private buildQueryString(params: ErrorQueryParams): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });
    
    return searchParams.toString();
  }

  /**
   * Get paginated list of error logs
   */
  async getAllErrors(params: ErrorQueryParams = {}): Promise<ErrorLog[]> {
    try {
      const queryString = this.buildQueryString(params);
      const url = `/api/v2/web/errors${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get<ErrorLog[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching error logs:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific error
   */
  async getErrorDetail(errorId: string): Promise<ErrorLogDetail> {
    try {
      const response = await apiClient.get<ErrorLogDetail>(`/api/v2/web/errors/${errorId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching error detail:', error);
      throw error;
    }
  }

  /**
   * Create a new error log entry
   */
  async createError(errorData: ErrorLogCreate): Promise<ErrorLog> {
    try {
      const response = await apiClient.post<ErrorLog>('/api/v2/web/errors', errorData);
      return response.data;
    } catch (error) {
      console.error('Error creating error log:', error);
      throw error;
    }
  }

  /**
   * Update an error log entry (nicht mehr benötigt da keine resolve functionality)
   */
  async deleteError(errorId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete<{ message: string }>(`/api/v2/web/errors/${errorId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting error log:', error);
      throw error;
    }
  }

  /**
   * Get error statistics overview
   */
  async getErrorStats(): Promise<ErrorStats> {
    try {
      const response = await apiClient.get<ErrorStats>('/api/v2/web/errors/stats/overview');
      return response.data;
    } catch (error) {
      console.error('Error fetching error stats:', error);
      throw error;
    }
  }

  /**
   * Log a client-side error automatically
   */
  async logClientError(error: Error, additionalInfo: {
    path?: string;
    userAgent?: string;
    userId?: string;
  } = {}): Promise<void> {
    try {
      const errorData: ErrorLogCreate = {
        method: 'CLIENT_ERROR',
        error_message: error.message,
        path: additionalInfo.path || window.location.pathname,
        stacktrace: error.stack,
  user_id: additionalInfo.userId,
        context_info: JSON.stringify({
          url: window.location.href,
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
          userAgent: additionalInfo.userAgent || navigator.userAgent
        })
      };

      // Use special logging endpoint that doesn't require admin permissions
      await apiClient.post('/api/v2/web/errors/log', errorData);
    } catch (loggingError) {
      // Don't throw if error logging fails - just log to console
      console.error('Failed to log client error:', loggingError);
    }
  }

  /**
   * Search errors by multiple criteria
   */
  async searchErrors(searchParams: {
    query?: string;
    method?: string;
    path?: string;
    dateRange?: {
      from: string;
      to: string;
    };
    limit?: number;
  }): Promise<ErrorLog[]> {
    const params: ErrorQueryParams = {
      limit: searchParams.limit || 100,
    };

    if (searchParams.query) {
      params.search = searchParams.query;
    }

    if (searchParams.method) {
      params.method = searchParams.method;
    }

    if (searchParams.path) {
      params.path = searchParams.path;
    }

    if (searchParams.dateRange) {
      params.date_from = searchParams.dateRange.from;
      params.date_to = searchParams.dateRange.to;
    }

    return this.getAllErrors(params);
  }
}

// Create and export a singleton instance
export const errorApi = new ErrorApi();

// Export types for use in components
export type {
  ErrorLog,
  ErrorLogDetail,
  ErrorLogCreate,
  ErrorStats,
  ErrorQueryParams
};

// Global error handler setup
export const setupGlobalErrorHandler = (userId?: string) => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorApi.logClientError(
      new Error(`Unhandled Promise Rejection: ${event.reason}`),
      {
        path: window.location.pathname,
        userId
      }
    );
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    errorApi.logClientError(
      new Error(event.message),
      {
        path: window.location.pathname,
        userId
      }
    );
  });
};

export default errorApi;
